import { DataStore } from "aws-amplify/datastore";
import { format } from "date-fns";
import { Client, Invoice, ServiceRendered } from "../../models";
import { ClientRepository } from "../interfaces";
import { ClientRecord, ClientDetailRecord, InvoiceRecord, ServiceRenderedRecord, PetRecord } from "../types";

const toClientRecord = (c: Client): ClientRecord => ({
  id: c.id,
  name: c.name,
  surname: c.surname,
  address: c.address,
  addressLine2: c.addressLine2,
  town: c.town,
  county: c.county,
  postCode: c.postCode,
  email: c.email,
  phoneNumber: c.phoneNumber,
  mobilePhone: c.mobilePhone,
  reference: c.reference,
  defaultMileage: c.defaultMileage,
  outstandingBalance: c.outstandingBalance,
  notes: c.notes,
});

const toServiceRenderedRecord = async (sr: ServiceRendered): Promise<ServiceRenderedRecord> => {
  const client = await sr.client;
  const service = await sr.service;
  return {
    id: sr.id,
    date: sr.date,
    invoiceID: sr.invoiceID,
    quantity: sr.quantity,
    mileage: sr.mileage,
    mileageCost: sr.mileageCost,
    serviceCost: sr.serviceCost,
    clientId: client.id,
    serviceId: service.id,
    serviceName: service.name,
  };
};

const toInvoiceRecord = async (inv: Invoice): Promise<InvoiceRecord> => ({
  id: inv.id,
  clientID: inv.clientID,
  invoiceName: inv.invoiceName,
  filePath: inv.filePath,
  startDate: inv.startDate,
  endDate: inv.endDate,
  subtotal: inv.subtotal,
  owner: inv.owner,
  label: inv.label,
  status: inv.status ?? null,
  servicesRendered: [],
});

export class DatastoreClientRepository implements ClientRepository {
  async list(): Promise<ClientRecord[]> {
    const clients = await DataStore.query(Client);
    clients.sort((a, b) => {
      const aValue = a.reference ?? a.surname;
      const bValue = b.reference ?? b.surname;
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    });
    return clients.map(toClientRecord);
  }

  async get(id: string): Promise<ClientDetailRecord> {
    const c = (await DataStore.query(Client, (q) => q.id.eq(id)))[0];
    const [invoicesRaw, servicesUsedRaw, petsRaw] = await Promise.all([
      c.invoices.toArray(),
      c.servicesUsed.toArray(),
      c.pet.toArray(),
    ]);

    const sortedInvoices = invoicesRaw.sort((a, b) =>
      a.startDate && b.startDate ? new Date(b.startDate).getTime() - new Date(a.startDate).getTime() : 0
    );

    const [invoices, servicesUsed] = await Promise.all([
      Promise.all(sortedInvoices.map(toInvoiceRecord)),
      Promise.all(servicesUsedRaw.map(toServiceRenderedRecord)),
    ]);

    const pets: PetRecord[] = await Promise.all(
      petsRaw.map(async (p) => {
        const vet = await p.vet;
        return {
          id: p.id,
          clientID: p.clientID,
          Name: p.Name,
          Type: p.Type,
          Breed: p.Breed,
          Sex: p.Sex,
          Neutered: p.Neutered,
          VaccinationDue: p.VaccinationDue,
          Notes: p.Notes,
          OffLead: p.OffLead,
          TermsAccepted: p.TermsAccepted,
          vetID: p.vetID,
          vet: vet ? { id: vet.id, name: vet.name, phoneNumber: vet.phoneNumber, address: vet.address } : null,
        };
      })
    );

    return { ...toClientRecord(c), invoices, servicesUsed, pets };
  }

  async save(client: Partial<ClientRecord> & { id?: string }): Promise<ClientRecord> {
    if (!client.id) {
      const c = new Client({
        name: client.name ?? "",
        surname: client.surname ?? "",
        ...client,
        invoices: [],
        pet: [],
        servicesUsed: [],
      });
      return toClientRecord(await DataStore.save(c));
    }
    const original = await DataStore.query(Client, client.id);
    if (!original) throw new Error("Unable to find client to update");
    const updated = await DataStore.save(
      Client.copyOf(original, (draft) => {
        Object.assign(draft, client);
      })
    );
    return toClientRecord(updated);
  }

  async delete(client: ClientRecord): Promise<void> {
    const original = await DataStore.query(Client, client.id);
    if (original) await DataStore.delete(original);
  }

  async getServicesRendered(clientId: string): Promise<ServiceRenderedRecord[]> {
    const srs = await DataStore.query(ServiceRendered, (s) => s.client.id.eq(clientId));
    return Promise.all(srs.map(toServiceRenderedRecord));
  }

  async loadForInvoicing(startDate: Date, endDate: Date): Promise<InvoiceRecord[]> {
    const start = format(startDate, "yyyy-MM-dd");
    const end = format(endDate, "yyyy-MM-dd");

    const existingInvoices = await DataStore.query(Invoice, (i) =>
      i.and((i) => [i.startDate.ge(start), i.endDate.le(end)])
    );

    const servicesRendered = await DataStore.query(ServiceRendered, (s) =>
      s.and((s) => [
        s.date.ge(start),
        s.date.le(end),
        s.or((s) => [s.invoiceID.eq(""), s.invoiceID.eq(null)]),
      ])
    );

    const completeInvoiceMap = new Map<string, { invoice: InvoiceRecord; srs: ServiceRendered[] }>();

    for (const sr of servicesRendered) {
      const clientId = (await sr.client).id;
      const srService = await sr.service;
      const cost = (sr.serviceCost ?? srService.cost) * sr.quantity + (sr.mileageCost ?? 0);

      if (!completeInvoiceMap.has(clientId)) {
        completeInvoiceMap.set(clientId, {
          invoice: {
            id: clientId,
            clientID: clientId,
            invoiceName: "",
            subtotal: cost,
            startDate: sr.date,
            endDate: sr.date,
            servicesRendered: [],
          },
          srs: [sr],
        });
      } else {
        const existing = completeInvoiceMap.get(clientId)!;
        const newSubtotal = (existing.invoice.subtotal ?? 0) + cost;
        const newStartDate =
          !existing.invoice.startDate || new Date(sr.date) < new Date(existing.invoice.startDate)
            ? sr.date
            : existing.invoice.startDate;
        const newEndDate =
          !existing.invoice.endDate || new Date(sr.date) > new Date(existing.invoice.endDate)
            ? sr.date
            : existing.invoice.endDate;
        existing.invoice = { ...existing.invoice, subtotal: newSubtotal, startDate: newStartDate, endDate: newEndDate };
        existing.srs.push(sr);
      }
    }

    const newInvoices = await Promise.all(
      Array.from(completeInvoiceMap.values()).map(async ({ invoice, srs }) => ({
        ...invoice,
        servicesRendered: await Promise.all(srs.map(toServiceRenderedRecord)),
      }))
    );

    const existingRecords = await Promise.all(existingInvoices.map(toInvoiceRecord));
    return [...newInvoices, ...existingRecords];
  }
}
